import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  Link as MuiLink,
} from '@mui/material';
import {
  ArrowForward as ArrowRightIcon,
  Business as BuildingIcon,
  BarChart as ChartBarIcon,
  AccessTime as ClockIcon,
  AutoAwesome as SparklesIcon,
  CheckCircleOutline as CircleCheckIcon,
  ShieldOutlined as ShieldCheckIcon,
  ChatBubbleOutline as MessageCircleIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

// Cores base para o tema da FazPedido
const PRIMARY_COLOR = '#7C3AED';
const HOVER_COLOR = '#5B21B6';
const BG_CARD_BORDER = '#E4DEFF';
const TEXT_DARK = '#1E1B4B';
const TEXT_MUTED = '#4B4A6C';

export default function FazPedidoAboutUs() {
  const stats = [
    {
      icon: <BuildingIcon sx={{ fontSize: 32, color: PRIMARY_COLOR }} />,
      title: '+500 negócios',
      description: 'publicam catálogos digitais atuais com o FazPedido',
    },
    {
      icon: <ChartBarIcon sx={{ fontSize: 32, color: PRIMARY_COLOR }} />,
      title: 'R$ 2,3 mi em 2025',
      description: 'em vendas atribuídas a catálogos criados na plataforma',
    },
    {
      icon: <ClockIcon sx={{ fontSize: 32, color: PRIMARY_COLOR }} />,
      title: '2 minutos',
      description: 'tempo médio para lançar um catálogo completo',
    },
    {
      icon: <SparklesIcon sx={{ fontSize: 32, color: PRIMARY_COLOR }} />,
      title: '+41% em conversão',
      description: 'crescimento médio após 30 dias usando automações da FazPedido',
    },
  ];

  const features = [
    {
      title: 'Criação de catálogos sem atrito',
      description:
        'Cadastro guiado com preview em tempo real, variações de cor e tamanho, upload em lote e publicação automática no link da loja.',
    },
    {
      title: 'WhatsApp como canal de vendas',
      description:
        'Templates inteligentes levam o produto certo para a conversa certa. O cliente recebe a mensagem com nome, preço, variação e link pronto para fechar a compra.',
    },
    {
      title: 'Dados para decidir rápido',
      description:
        'Métricas de visualizações, cliques e produtos mais quentes alimentam alertas de reposição e campanhas segmentadas.',
    },
    {
      title: 'Personalização real da vitrine',
      description:
        'Logos, banners, paleta da marca, histórias da loja e seções extras transformam o catálogo em uma experiência profissional. Sem código.',
    },
  ];

  const infrastructure = [
    {
      icon: <ShieldCheckIcon sx={{ fontSize: 28, color: PRIMARY_COLOR }} />,
      title: 'Infraestrutura confiável',
      description:
        'Autenticação, dados e imagens hospedados em servidor próprio em São Paulo, com replicação, backups contínuos e monitoramento 24/7 para garantir total alta disponibilidade para o FazPedido.',
    },
    {
      icon: <SparklesIcon sx={{ fontSize: 28, color: PRIMARY_COLOR }} />,
      title: 'Equipe focada em digital commerce',
      description:
        'Especialistas em jornadas de venda online e presencial trabalham lado a lado com clientes para validar recursos e compartilhar boas práticas.',
    },
    {
      icon: <MessageCircleIcon sx={{ fontSize: 28, color: PRIMARY_COLOR }} />,
      title: 'Suporte humano em até 2h',
      description:
        'Atendimento de segunda a sexta, das 09h às 18h (BRT), por email e WhatsApp com especialistas que acompanham todo o onboarding.',
    },
    {
      icon: <CircleCheckIcon sx={{ fontSize: 28, color: PRIMARY_COLOR }} />,
      title: 'Integrações e automações',
      description:
        'Link curto, QR Code, captura de leads e conectores com páginas, planilhas e ferramentas de CRM para manter operações em sincronia.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#FAF9FF' }}>
      {/* Header Section */}
      <Box
        component="header"
        sx={{
          borderBottom: '1px solid rgba(232, 227, 255, 0.7)',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(8px)',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 4, md: 5 } }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: PRIMARY_COLOR,
                fontSize: '0.875rem',
              }}
            >
              Sobre nós
            </Typography>

            <Grid container spacing={5} alignItems="center">
              <Grid item xs={12} md={10}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                      fontWeight: 'bold',
                      color: TEXT_DARK,
                      fontSize: { xs: '1.875rem', sm: '2.25rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    FazPedido: catálogos digitais que vendem todos os dias
                  </Typography>

                  <Typography variant="body1" sx={{ color: TEXT_MUTED, maxWidth: '800px', fontSize: '1rem' }}>
                    Construímos experiências digitais que combinam publicação rápida, atendimento automatizado e dados em tempo real. A FazPedido atende negócios de todos os segmentos que desejam transformar links em vendas.
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mt: 1 }}>
                    <Button
                      variant="contained"
                      component={Link}
                      to="/auth/register"
                      endIcon={<ArrowRightIcon />}
                      sx={{
                        borderRadius: '50px',
                        backgroundColor: PRIMARY_COLOR,
                        color: '#fff',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        py: 1.25,
                        boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
                        '&:hover': {
                          backgroundColor: HOVER_COLOR,
                        },
                      }}
                    >
                      Criar minha conta
                    </Button>

                    <Button
                      variant="outlined"
                      href="mailto:suporte@fazpedido.com.br"
                      sx={{
                        borderRadius: '50px',
                        borderColor: PRIMARY_COLOR,
                        color: PRIMARY_COLOR,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2.5,
                        py: 1,
                        '&:hover': {
                          borderColor: HOVER_COLOR,
                          backgroundColor: 'rgba(124, 58, 237, 0.04)',
                        },
                      }}
                    >
                      Falar com especialista
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container component="main" maxWidth="lg" sx={{ py: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>

        {/* Metric Cards Grid */}
        <Grid container spacing={3}>
          {stats.map((stat, index) => (
            <Grid item size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  borderColor: BG_CARD_BORDER,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                }}
              >
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
                  {stat.icon}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: TEXT_DARK, fontSize: '1.125rem' }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
                      {stat.description}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Feature Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', color: TEXT_DARK, fontSize: '1.5rem' }}>
            Como a FazPedido impulsiona seu negócio
          </Typography>
          <Typography variant="body1" sx={{ color: TEXT_MUTED, maxWidth: '800px' }}>
            Atendemos equipes comerciais, consultores independentes e empresas físicas ou digitais que precisam mostrar produtos e serviços com velocidade. Nossos fluxos reduzem atrito, simplificam atendimento e colocam dados úteis na sua tomada de decisão.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            {features.map((feature, index) => (
              <Card
                key={index}
                variant="outlined"
                sx={{
                  borderRadius: 4,
                  borderColor: BG_CARD_BORDER,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                }}
              >
                <CardContent sx={{ p: 2.5, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <CircleCheckIcon sx={{ color: PRIMARY_COLOR, mt: 0.5 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: TEXT_DARK, fontSize: '1.125rem' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: TEXT_MUTED, mt: 0.5 }}>
                      {feature.description}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Infrastructure Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', color: TEXT_DARK, fontSize: '1.5rem' }}>
            Infraestrutura e diferenciais
          </Typography>
          <Typography variant="body1" sx={{ color: TEXT_MUTED, maxWidth: '800px' }}>
            O FazPedido foi desenvolvido utilizando as tecnologias mais modernas e eficientes do mercado digital. A nossa aplicação combina a agilidade do Node.js no backend para o processamento de requisições em tempo real com a interatividade e fluidez do React JS no frontend.  </Typography>

          <Grid container spacing={3}>
            {infrastructure.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    borderColor: BG_CARD_BORDER,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  }}
                >
                  <CardContent sx={{ p: 3, display: 'flex', gap: 2, height: '100%' }}>
                    {item.icon}
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: TEXT_DARK, fontSize: '1.125rem' }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: TEXT_MUTED, mt: 0.5 }}>
                        {item.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Community and CTA Section */}
        <Card
          variant="outlined"
          sx={{
            borderRadius: 5,
            borderColor: BG_CARD_BORDER,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            p: { xs: 1, md: 2 },
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Grid container spacing={4}>
              <Grid item xs={12} lg={6.5} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: TEXT_DARK }}>
                  Comunidade e suporte
                </Typography>
                <Typography variant="body1" sx={{ color: TEXT_MUTED }}>
                  Conte com materiais de apoio, central de ajuda, vídeos curtos e suporte humano sempre que precisar. Respondemos em até duas horas úteis e acompanhamos a publicação do primeiro catálogo com você.
                </Typography>

                <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                  <ListItem disableGutters sx={{ py: 0 }}>
                    <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
                      <strong>Central de Ajuda:</strong> guias passo a passo, resolução rápida de dúvidas e roadmap público.
                    </Typography>
                  </ListItem>
                  <ListItem disableGutters sx={{ py: 0 }}>
                    <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
                      <strong>Suporte:</strong> suporte@fazpedido.com.br · WhatsApp (11) 9 8179-9101 · Segunda à sexta, 09h às 18h.
                    </Typography>
                  </ListItem>
                  <ListItem disableGutters sx={{ py: 0 }}>
                    <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
                      <strong>Comunidade:</strong> Conteúdos semanais no Instagram com ideias de campanhas, automações e destaques de negócios reais.
                    </Typography>
                  </ListItem>
                </List>
              </Grid>

              <Grid item xs={12} lg={5.5} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: TEXT_DARK }}>
                  Pronto para começar?
                </Typography>
                <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
                  Cadastre-se, importe seus produtos e publique no link{' '}
                  <MuiLink component="span" sx={{ fontWeight: 600, color: PRIMARY_COLOR, textDecoration: 'none' }}>
                    fazpedido.com.br/sua-loja
                  </MuiLink>
                  . Em poucos minutos você terá um catálogo que inspira confiança e gera pedidos imediatos pelo WhatsApp.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mt: 1 }}>
                  <Button
                    variant="contained"
                    component={Link}
                    to="/auth/register"
                    fullWidth
                    sx={{
                      borderRadius: '50px',
                      backgroundColor: PRIMARY_COLOR,
                      color: '#fff',
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.25,
                      boxShadow: '0 4px 12px rgba(124, 58, 237, 0.2)',
                      '&:hover': {
                        backgroundColor: HOVER_COLOR,
                      },
                    }}
                  >
                    Criar conta gratuita
                  </Button>

                  <Button
                    variant="outlined"
                    href="/catalog-demo"
                    fullWidth
                    sx={{
                      borderRadius: '50px',
                      borderColor: PRIMARY_COLOR,
                      color: PRIMARY_COLOR,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.25,
                      '&:hover': {
                        borderColor: HOVER_COLOR,
                        backgroundColor: 'rgba(124, 58, 237, 0.04)',
                      },
                    }}
                  >
                    Ver catálogo exemplo
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

      </Container>
    </Box>
  );
}